import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=A%20clean%20minimal%20cybersecurity%20concept%20image%20showing%20a%20laptop%20screen%20displaying%20warning%20signs%20with%20a%20shield%20icon%2C%20warm%20amber%20and%20soft%20gray%20tones%2C%20professional%20editorial%20style%20on%20light%20background%2C%20high%20detail%2C%20subtle%20gradients&width=1200&height=600&seq=article-detail-1&orientation=landscape',
  '2': 'https://readdy.ai/api/search-image?query=A%20minimal%20digital%20security%20image%20featuring%20a%20smartphone%20with%20a%20download%20warning%20symbol%2C%20surrounded%20by%20protective%20shield%20elements%2C%20warm%20amber%20accents%20on%20a%20clean%20light%20gray%20background%2C%20editorial%20tech%20style&width=1200&height=600&seq=article-detail-2&orientation=landscape',
  '3': 'https://readdy.ai/api/search-image?query=An%20abstract%20privacy%20protection%20concept%2C%20a%20silhouette%20with%20a%20lock%20icon%20overlay%2C%20soft%20green%20and%20warm%20gray%20tones%2C%20clean%20minimal%20composition%20on%20light%20background%2C%20editorial%20photography%20style&width=1200&height=600&seq=article-detail-3&orientation=landscape',
  '4': 'https://readdy.ai/api/search-image?query=A%20minimalist%20comparison%20image%20showing%20two%20smartphones%20side%20by%20side%2C%20one%20displaying%20a%20genuine%20checkmark%20and%20the%20other%20a%20warning%20cross%20mark%2C%20clean%20light%20background%20with%20soft%20teal%20and%20amber%20accents%2C%20editorial%20tech%20photography&width=1200&height=600&seq=article-detail-4&orientation=landscape',
  '5': 'https://readdy.ai/api/search-image?query=A%20minimal%20law%20and%20digital%20concept%2C%20a%20gavel%20next%20to%20a%20laptop%2C%20soft%20warm%20gray%20and%20subtle%20green%20tones%2C%20clean%20editorial%20style%20on%20light%20background%2C%20professional%20composition&width=1200&height=600&seq=article-detail-5&orientation=landscape',
  '6': 'https://readdy.ai/api/search-image?query=A%20minimal%20concept%20image%20of%20online%20scam%20awareness%2C%20a%20fishing%20hook%20disguised%20as%20a%20play%20button%20on%20a%20screen%2C%20warm%20amber%20warning%20tones%20on%20clean%20light%20background%2C%20editorial%20tech%20style%2C%20high%20detail&width=1200&height=600&seq=article-detail-6&orientation=landscape',
  '7': 'https://readdy.ai/api/search-image?query=A%20clean%20minimal%20tutorial%20concept%2C%20a%20laptop%20displaying%20security%20settings%20interface%20with%20step-by-step%20indicators%2C%20soft%20teal%20accents%20on%20light%20background%2C%20editorial%20tech%20photography%20style%2C%20high%20detail&width=1200&height=600&seq=article-detail-7&orientation=landscape',
  '8': 'https://readdy.ai/api/search-image?query=A%20minimal%20FAQ%20concept%20image%2C%20speech%20bubbles%20with%20question%20marks%20in%20a%20clean%20organized%20layout%2C%20warm%20amber%20and%20soft%20gray%20tones%2C%20editorial%20style%20on%20light%20background%2C%20high%20detail&width=1200&height=600&seq=article-detail-8&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles
        .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的文章可能已被删除或不存在</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    image: articleImages[article.id],
    url: `https://example.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '安全资讯中心',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://example.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden mb-6">
              <img
                src={articleImages[article.id]}
                alt={article.title}
                title={`${article.title} - 安全资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">热门</span>
              )}
              {article.isNew && (
                <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">新发布</span>
              )}
            </div>

            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> 发布于 {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line"></i> 更新于 {article.updateDate}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-md border border-background-200/70 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="p-4 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1 hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{related.summary}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}